'use client'

import React, { useState, useEffect } from 'react'
import { Card } from '@/components/ui/Card'

interface PerformanceMetrics {
  conversationStartTime: number
  aiResponseTime: number
  dbQueryTime: number
  streamingTime?: number
  totalResponseTime: number
}

// Performance monitoring dashboard for conversation optimization
export function PerformanceDashboard() {
  const [metrics, setMetrics] = useState<PerformanceMetrics[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_currentMetric, setCurrentMetric] = useState<Partial<PerformanceMetrics>>({}) // Reserved for future feature
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Listen for performance events from the conversation system
    const handlePerformanceEvent = (event: CustomEvent) => {
      const { type, timestamp, duration } = event.detail
      
      setCurrentMetric(prev => {
        const updated = { ...prev }
        
        switch (type) {
          case 'conversation_start':
            updated.conversationStartTime = timestamp
            break
          case 'ai_response_complete':
            updated.aiResponseTime = duration
            break
          case 'db_query_complete':
            updated.dbQueryTime = duration
            break
          case 'streaming_complete':
            updated.streamingTime = duration
            break
          case 'total_response_complete':
            updated.totalResponseTime = duration
            // Save completed metric
            setMetrics(prev => [...prev.slice(-9), updated as PerformanceMetrics])
            setCurrentMetric({})
            break
        }
        
        return updated
      })
    }

    window.addEventListener('conversation-performance', handlePerformanceEvent as EventListener)
    
    return () => {
      window.removeEventListener('conversation-performance', handlePerformanceEvent as EventListener)
    }
  }, [])

  // Toggle visibility with keyboard shortcut (Ctrl+Shift+P)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        setIsVisible(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  if (!isVisible) return null

  const avgResponseTime = metrics.length > 0 
    ? metrics.reduce((sum, m) => sum + m.totalResponseTime, 0) / metrics.length 
    : 0

  const lastMetric = metrics[metrics.length - 1]

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <Card className="p-4 bg-slate-900 text-white text-xs font-mono shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-emerald-400">Performance Monitor</h3>
          <button
            onClick={() => setIsVisible(false)}
            className="text-slate-400 hover:text-white"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Target:</span>
            <span className="text-emerald-400">2000ms</span>
          </div>
          
          <div className="flex justify-between">
            <span>Average:</span>
            <span className={avgResponseTime <= 2000 ? 'text-emerald-400' : 'text-red-400'}>
              {avgResponseTime.toFixed(0)}ms
            </span>
          </div>
          
          {lastMetric && (
            <>
              <div className="border-t border-slate-700 pt-2 mt-2">
                <div className="text-slate-300 mb-1">Last Response:</div>
                
                <div className="flex justify-between">
                  <span>AI Response:</span>
                  <span>{lastMetric.aiResponseTime}ms</span>
                </div>
                
                <div className="flex justify-between">
                  <span>DB Queries:</span>
                  <span>{lastMetric.dbQueryTime}ms</span>
                </div>
                
                {lastMetric.streamingTime && (
                  <div className="flex justify-between">
                    <span>Streaming:</span>
                    <span>{lastMetric.streamingTime}ms</span>
                  </div>
                )}
                
                <div className="flex justify-between border-t border-slate-700 pt-1 mt-1">
                  <span className="font-bold">Total:</span>
                  <span className={`font-bold ${
                    lastMetric.totalResponseTime <= 2000 ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {lastMetric.totalResponseTime}ms
                  </span>
                </div>
              </div>
            </>
          )}
          
          {metrics.length > 0 && (
            <div className="border-t border-slate-700 pt-2 mt-2">
              <div className="text-slate-300 mb-1">Recent Trend:</div>
              <div className="flex gap-1">
                {metrics.slice(-10).map((metric, index) => (
                  <div
                    key={index}
                    className={`w-2 h-8 rounded ${
                      metric.totalResponseTime <= 2000 
                        ? 'bg-emerald-500' 
                        : metric.totalResponseTime <= 3000
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    title={`${metric.totalResponseTime}ms`}
                    style={{
                      height: `${Math.min(32, (metric.totalResponseTime / 5000) * 32)}px`
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="text-slate-400 text-xs mt-3 pt-2 border-t border-slate-700">
          Press Ctrl+Shift+P to toggle
        </div>
      </Card>
    </div>
  )
}

// Helper function to emit performance events
export function emitPerformanceEvent(type: string, data: Record<string, unknown>) {
  window.dispatchEvent(new CustomEvent('conversation-performance', {
    detail: { type, timestamp: Date.now(), ...data }
  }))
}